'use client'

import { Box, Chip } from '@mui/material'
import { useStatsigUser } from '@statsig/react-bindings'
import { useCallback, useEffect } from 'react'

import { useStatsigGate } from '~/configs/statsig'

import FeatureFlagsPanel from './FeatureFlagsPanel'
import { ANONYMOUS_STATSIG_USER_ID } from './gateOverrides'
import useGateOverrides from './useGateOverrides'
import useFeatureFlagsPanel from './useFeatureFlagsPanel'

/**
 * Mounts the feature flag panel and its two entry points for Synop-internal
 * users. Renders no persistent chrome: the only thing on screen is the
 * override indicator, and only while overrides are actually in effect.
 *
 * Must sit inside the Statsig provider — unlike `BackendSwitcher`, which reads
 * build config and can live above it.
 */
export default function FeatureFlagsLauncher() {
  const { enabled: isSynopInternal, isLoading } = useStatsigGate('synop_internal_only')
  const { isOpen, open, close, toggle } = useFeatureFlagsPanel()
  const { overriddenCount, resetAll } = useGateOverrides()
  const { user } = useStatsigUser()

  // Someone who cannot reach this panel must not be left with forced gates and
  // no way to undo them — the case where a staff member loses internal access
  // mid-session. Waits for a resolved, non-placeholder identity, because the
  // provider evaluates as `unknown_user` until Auth0 answers and the gate reads
  // false throughout that window.
  const isIdentified = Boolean(user.userID) && user.userID !== ANONYMOUS_STATSIG_USER_ID
  useEffect(() => {
    if (isLoading || !isIdentified || isSynopInternal) return
    if (overriddenCount > 0) resetAll()
  }, [isLoading, isIdentified, isSynopInternal, overriddenCount, resetAll])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Cmd/Ctrl+Shift+. — unbound in Chrome, Safari and Firefox, unlike the
      // Cmd+Shift+F/G pairs that browsers reserve for find-in-page.
      //
      // Matched on `code` (physical key) rather than `key`, which reports the
      // shifted character `>` while Shift is held and so would never equal '.'.
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.code === 'Period') {
        event.preventDefault()
        toggle()
      }
    },
    [toggle]
  )

  useEffect(() => {
    if (!isSynopInternal) return undefined
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isSynopInternal, handleKeyDown])

  if (!isSynopInternal) return null

  return (
    <>
      {overriddenCount > 0 && !isOpen && (
        <Box
          sx={{
            position: 'fixed',
            // Rides above the assistant bar, which publishes its measured height
            // on <html> while mounted, and above the backend chip when both show.
            bottom: 'calc(var(--synop-assistant-bar-height, 0px) + 56px)',
            right: 16,
            zIndex: (theme) => theme.zIndex.tooltip + 1,
            transition: 'bottom .25s ease-in-out'
          }}
        >
          <Chip
            size="small"
            color="warning"
            clickable
            onClick={open}
            label={`${overriddenCount} flag${overriddenCount === 1 ? '' : 's'} overridden`}
            sx={{ boxShadow: 3, fontWeight: 600 }}
            aria-label={`${overriddenCount} feature flags overridden. Click to manage.`}
          />
        </Box>
      )}

      <FeatureFlagsPanel open={isOpen} onClose={close} />
    </>
  )
}
